import {defineStore} from 'pinia';
import {useOidcStore} from './oidcStore';
import {ref, toRef, type Ref, computed} from 'vue';
import {useSecurityStorage} from '../shared/composables/useSecurityStorage';
import UnauthenticatedError from '../api/helpers/UnauthenticatedError';
import SecurityRepository from '../api/repositories/SecurityRepository';
import {useInformationStore} from './informationStore';
import jwtDecode from 'jwt-decode';

type DecodedToken = {
    exp?: number;
    iat?: number;
    [key: string]: unknown;
};

export const useSecurityStore = defineStore('security', () => {
    /**
     * Standard authentication token
     */
    const token = useSecurityStorage('security.token');

    /**
     * Standard authentication refreshToken
     */
    const refreshToken = useSecurityStorage('security.refreshToken');

    const oidcStore = useOidcStore();

    /**
     * Decoded JWT token with typed payload
     */
    const decodedToken = computed<DecodedToken | null>(() => {
        const effectiveToken = oidcStore.idToken || oidcStore.accessToken || token.value;
        if (!effectiveToken) {
            return null;
        }
        try {
            const decoded = jwtDecode<DecodedToken>(effectiveToken);
            if (decoded && typeof decoded === 'object') {
                return decoded;
            }
        } catch {
            // Invalid token
        }
        return null;
    });

    /**
     * Computed token expiry time (in milliseconds)
     */
    const tokenExpiry = computed<number | null>(() => {
        if (!decodedToken.value?.exp) {
            return null;
        }
        // JWT exp is in seconds, convert to milliseconds
        return decodedToken.value.exp * 1_000;
    });

    /**
     * Original token duration in milliseconds (time between iat and exp when issued)
     */
    const tokenExpiryDuration = computed<number | null>(() => {
        if (!decodedToken.value?.exp || !decodedToken.value?.iat) {
            return null;
        }
        // Return original duration in milliseconds
        return (decodedToken.value.exp - decodedToken.value.iat) * 1_000;
    });

    const lastRefreshAttempt = ref<Date | null>(null);

    /**
     * The authentication method, when standard authentication isn't used.
     */
    const authType = useSecurityStorage('security.authType');

    /**
     * Whether MSAL is used.
     */
    const msal = useSecurityStorage<boolean>('security.msal', false);

    /**
     * Whether MSAL is blocked.
     */
    const blockMsal = useSecurityStorage<boolean>('security.block_msal', false);
    const msalHandled = useSecurityStorage<boolean>('security.msal_handled', false);

    /**
     * Clears all security related data.
     */
    function reset() {
        token.value = null;
        refreshToken.value = null;
        authType.value = null;
        msal.value = false;
        blockMsal.value = false;
        msalHandled.value = false;
    }

    /**
     * Clears token and OIDC tokens.
     * @param withRefresh When set, also clears the refreshToken
     */
    function clearToken(withRefresh = false) {
        oidcStore.clearOidc();
        token.value = null;
        authType.value = null;

        if (withRefresh) {
            refreshToken.value = null;
        }
    }

    const {refreshAccessToken, isRefreshingToken} = useRefreshAccessToken(
        token,
        refreshToken,
        toRef(() => oidcStore.hasOidcToken),
        clearToken
    );

    return {
        token,
        refreshToken,
        decodedToken,
        tokenExpiry,
        tokenExpiryDuration,
        authType,
        msal,
        blockMsal,
        msalHandled,
        isRefreshingToken,
        clearToken,
        reset,
        refreshAccessToken,
        lastRefreshAttempt,
    };
});

function useRefreshAccessToken(
    token: Ref<string | null>,
    refreshToken: Ref<string | null>,
    hasOidcToken: Ref<boolean>,
    clearToken: () => void
) {
    const isRefreshingToken = ref(false);

    /**
     *
     * @param shouldClearTokens Whether access tokens should be cleared before refreshing
     * @throws UnauthenticatedError When the refresh failed
     */
    async function refreshAccessToken(shouldClearTokens = false): Promise<boolean> {
        if (isRefreshingToken.value) {
            return false;
        }

        if (!shouldClearTokens && (token.value || hasOidcToken.value)) {
            await useInformationStore().refresh();
            return false;
        }

        isRefreshingToken.value = true;

        if (shouldClearTokens) {
            clearToken();
        }

        try {
            if (!refreshToken.value) {
                throw new UnauthenticatedError();
            }

            const value = await SecurityRepository().refresh(refreshToken.value);

            token.value = value.token;
            refreshToken.value = value.refreshToken;
        } finally {
            isRefreshingToken.value = false;
        }

        return true;
    }

    return {
        refreshAccessToken,
        isRefreshingToken,
    };
}
